import React from "react";
import { Card, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Placeholder data - will be replaced by API calls
const TENANT_USAGE = [
  { name: "Acme Corp", dau: 320, apiCalls: "2.4M", storage: "45.2 GB", storagePercent: 45, healthScore: 92, churnRisk: "Low" },
  { name: "Globex Industries", dau: 1200, apiCalls: "8.1M", storage: "312 GB", storagePercent: 78, healthScore: 88, churnRisk: "Low" },
  { name: "Initech LLC", dau: 8, apiCalls: "12K", storage: "2.1 GB", storagePercent: 21, healthScore: 45, churnRisk: "High" },
  { name: "Wayne Media", dau: 180, apiCalls: "890K", storage: "28 GB", storagePercent: 28, healthScore: 76, churnRisk: "Medium" },
  { name: "Stark Streaming", dau: 4200, apiCalls: "15.2M", storage: "890 GB", storagePercent: 89, healthScore: 71, churnRisk: "Low" },
  { name: "Hooli TV", dau: 5, apiCalls: "3K", storage: "120 GB", storagePercent: 12, healthScore: 20, churnRisk: "High" },
];

const COHORT_DATA = [
  { plan: "Starter", tenants: 2, avgDau: 30, avgHealth: 55, avgApi: "50K" },
  { plan: "Growth", tenants: 2, avgDau: 250, avgHealth: 72, avgApi: "600K" },
  { plan: "Business", tenants: 2, avgDau: 1100, avgHealth: 80, avgApi: "4.5M" },
  { plan: "Enterprise", tenants: 2, avgDau: 4500, avgHealth: 88, avgApi: "12M" },
];

function ChurnRiskChip({ risk }) {
  const colorMap = { Low: "success", Medium: "warning", High: "error" };
  return (
    <MDTypography
      variant="caption"
      fontWeight="bold"
      color={colorMap[risk] || "text"}
    >
      {risk}
    </MDTypography>
  );
}

function Analytics() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3}>
        {/* Page Header */}
        <MDBox mb={2.5}>
          <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
            <span style={{ color: "#5D6D7E", fontSize: 12 }}>Control Plane</span>
            <span style={{ color: "#BDC3C7" }}>›</span>
            <span style={{ color: "#1E3A5F", fontSize: 12, fontWeight: 600 }}>Analytics</span>
          </MDBox>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1E3A5F" }}>Usage Analytics</h1>
        </MDBox>

        {/* Summary Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <MDBox p={2} textAlign="center">
                <MDTypography variant="h3" color="info">5,915</MDTypography>
                <MDTypography variant="caption" color="text">Total DAU (all tenants)</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <MDBox p={2} textAlign="center">
                <MDTypography variant="h3" color="success">26.6M</MDTypography>
                <MDTypography variant="caption" color="text">API Calls (30d)</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <MDBox p={2} textAlign="center">
                <MDTypography variant="h3" color="warning">72%</MDTypography>
                <MDTypography variant="caption" color="text">Avg Health Score</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <MDBox p={2} textAlign="center">
                <MDTypography variant="h3" color="error">2</MDTypography>
                <MDTypography variant="caption" color="text">High Churn Risk</MDTypography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Per-Tenant Usage Table */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={3} pb={0}>
            <MDTypography variant="h5" fontWeight="bold" color="dark">
              Tenant Usage Analytics
            </MDTypography>
          </MDBox>
          <MDBox pt={3} px={2} pb={2}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant</TableCell>
                    <TableCell align="right">DAU</TableCell>
                    <TableCell align="right">API Calls</TableCell>
                    <TableCell>Storage</TableCell>
                    <TableCell align="center">Health</TableCell>
                    <TableCell align="center">Churn Risk</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {TENANT_USAGE.map((t) => (
                    <TableRow key={t.name}>
                      <TableCell>
                        <MDTypography variant="button" fontWeight="medium">{t.name}</MDTypography>
                      </TableCell>
                      <TableCell align="right">{t.dau.toLocaleString()}</TableCell>
                      <TableCell align="right">{t.apiCalls}</TableCell>
                      <TableCell>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={t.storagePercent}
                            color={t.storagePercent > 80 ? "error" : t.storagePercent > 60 ? "warning" : "success"}
                            sx={{ width: 60, height: 6, borderRadius: 3 }}
                          />
                          <MDTypography variant="caption">{t.storage}</MDTypography>
                        </MDBox>
                      </TableCell>
                      <TableCell align="center">
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color={t.healthScore >= 80 ? "success" : t.healthScore >= 60 ? "warning" : "error"}
                        >
                          {t.healthScore}
                        </MDTypography>
                      </TableCell>
                      <TableCell align="center">
                        <ChurnRiskChip risk={t.churnRisk} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>

        {/* Cohort Analysis */}
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Cohort Analysis (by Plan)
            </MDTypography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Plan</TableCell>
                    <TableCell align="center">Tenants</TableCell>
                    <TableCell align="right">Avg DAU</TableCell>
                    <TableCell align="center">Avg Health</TableCell>
                    <TableCell align="right">Avg API Calls/mo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {COHORT_DATA.map((c) => (
                    <TableRow key={c.plan}>
                      <TableCell>
                        <MDTypography variant="button" fontWeight="medium">{c.plan}</MDTypography>
                      </TableCell>
                      <TableCell align="center">{c.tenants}</TableCell>
                      <TableCell align="right">{c.avgDau.toLocaleString()}</TableCell>
                      <TableCell align="center">{c.avgHealth}</TableCell>
                      <TableCell align="right">{c.avgApi}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analytics;
